"use client";

import { useState } from "react";
import { useTrialForm } from "@/components/providers/TrialFormProvider";
import { useContactForm } from "@/components/providers/ContactFormProvider";

const tiers = [
  {
    name: "Core",
    description:
      "For individual analysts and researchers who need error-free AI search and SEC filing analysis.",
    monthlyPrice: 119,
    annualPrice: 99,
    cta: { label: "Start free trial", action: "trial" as const },
    featured: false,
    features: [
      { text: "Market-wide AI search with Co-Analyst", bold: true },
      { text: "Restatement-adjusted numbers", bold: true },
      { text: "Guidance model", bold: true },
      { text: "Pre-built & custom agents", bold: true },
      { text: "Every U.S. public filer", bold: true },
      { text: "Research Library access", bold: true },
    ],
  },
  {
    name: "Institutional",
    description:
      "For institutional teams who need higher usage limits, export capabilities, and dedicated support.",
    monthlyPrice: null,
    annualPrice: null,
    cta: { label: "Contact us", action: "contact" as const },
    featured: true,
    features: [
      { text: "Everything in Core", bold: true },
      { text: "5x the usage", bold: true },
      { text: "Proprietary forensic risk analysis and red flags", bold: true },
      { text: "Unlimited agentic automations", bold: true },
      { text: "Team sharing and export features", bold: true },
      { text: "Dedicated support", bold: true },
    ],
  },
];

export default function PricingCards() {
  const { openTrialForm } = useTrialForm();
  const { openContactForm } = useContactForm();
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <div>
      {/* Cards */}
      <div className="grid md:grid-cols-2 md:grid-rows-[auto_auto_auto_1fr] gap-8 max-w-4xl mx-auto">
        {tiers.map((tier) => (
          <div
            key={tier.name}
            className={`relative rounded-lg lg:rounded-xl border p-8 sm:p-10 transition-all duration-500 grid grid-rows-subgrid row-span-4 ${
              tier.featured
                ? "border-black/20 bg-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)]"
                : "border-black/[0.04] bg-white hover:border-black/[0.08]"
            }`}
          >
            {tier.featured && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center px-3 py-1 text-xs font-medium tracking-wider uppercase bg-black text-white rounded-full">
                  Most Popular
                </span>
              </div>
            )}

            <div className="mb-4">
              <h3 className="text-xl font-semibold tracking-tight mb-2">
                {tier.name}
              </h3>
              <p className="text-sm text-black/40 font-light leading-relaxed">
                {tier.description}
              </p>
            </div>

            <div className="mb-8">
              {tier.monthlyPrice !== null ? (
                <>
                  {/* Billing toggle — Core only */}
                  <div className="flex flex-col items-start gap-2 mb-5">
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-sm transition-colors duration-300 ${
                          !isAnnual ? "text-black font-medium" : "text-black/30"
                        }`}
                      >
                        Monthly
                      </span>
                      <button
                        onClick={() => setIsAnnual(!isAnnual)}
                        className={`relative w-14 h-7 rounded-full p-0.5 transition-colors duration-300 ${isAnnual ? "bg-black" : "bg-black/20"}`}
                        aria-label="Toggle billing period"
                      >
                        <span
                          className={`block w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-300 ${
                            isAnnual ? "translate-x-7" : "translate-x-0"
                          }`}
                        />
                      </button>
                      <span
                        className={`text-sm transition-colors duration-300 ${
                          isAnnual ? "text-black font-medium" : "text-black/30"
                        }`}
                      >
                        Annual
                      </span>
                    </div>
                    <span className={`text-xs font-medium text-black bg-black/[0.04] px-2 py-0.5 rounded-full transition-opacity duration-300 ${isAnnual ? "opacity-100" : "opacity-0"}`}>
                      Save 2 months (20% off)
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl sm:text-5xl font-[family-name:var(--font-display)] tracking-tight text-black">
                      ${isAnnual ? tier.annualPrice : tier.monthlyPrice}
                    </span>
                    <span className="text-sm text-black/30 font-light">/month</span>
                  </div>
                  <p className="text-xs text-black/40 mt-2">
                    {isAnnual
                      ? `$${tier.annualPrice! * 12}/year, billed annually`
                      : "Billed monthly"}
                  </p>
                </>
              ) : (
                <div>
                  <span className="text-3xl sm:text-4xl font-[family-name:var(--font-display)] tracking-tighter text-black">
                    Contact our sales team
                  </span>
                </div>
              )}
            </div>

            {tier.cta.action === "trial" ? (
              <a
                href="https://platform.hudson-labs.com/auth/signup"
                className={`self-start block w-full text-center py-3 px-6 rounded-full text-sm font-medium transition-all duration-500 bg-black text-white hover:bg-neutral-800`}
              >
                {tier.cta.label}
              </a>
            ) : (
              <button
                onClick={openContactForm}
                className={`self-start block w-full text-center py-3 px-6 rounded-full text-sm font-medium transition-all duration-500 bg-black text-white hover:bg-neutral-800`}
              >
                {tier.cta.label}
              </button>
            )}

            <ul className="space-y-3 pt-8">
              {tier.features.map((feature) => (
                <li
                  key={feature.text}
                  className="flex items-start gap-3 text-sm text-black/50"
                >
                  <svg
                    className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      tier.featured ? "text-black" : "text-black/40"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                  <span className={feature.bold ? "font-medium text-black/70" : "font-light"}>{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
